import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, FileText, Link, FileCode, Plus, Trash2, RefreshCw, X, Loader2, Upload, AlertCircle, Edit3, Save } from 'lucide-react';
import { haptic } from '../../telegram';
import { documentsApi } from '../../api/documentsApi';
import type { DocumentDto, DocumentKind } from '../../api/documentsApi';

const KIND_ICONS: Record<string, any> = { file: FileText, url: Link, text: FileCode };

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { const r = reader.result as string; resolve(r.split(',')[1] ?? ''); };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function Knowledge() {
  const nav = useNavigate();
  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addKind, setAddKind] = useState<DocumentKind>('url');
  const [addUrl, setAddUrl] = useState('');
  const [addText, setAddText] = useState('');
  const [addName, setAddName] = useState('');
  const [fileRef, setFileRef] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () => { documentsApi.list().then((d) => { setDocs(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const statusLabel = (s: string) => s === 'ready' ? 'Готов' : s === 'indexing' ? 'Индексируется' : s === 'pending' ? 'В очереди' : s === 'error' ? 'Ошибка' : s;

  const resetForm = () => { setAdding(false); setEditingId(null); setAddUrl(''); setAddText(''); setAddName(''); setFileRef(null); setError(''); };
  const startAdd = () => { haptic('light'); resetForm(); setAdding(true); setAddKind('url'); };
  const startEdit = (d: DocumentDto) => { haptic('light'); setAdding(true); setEditingId(d.id); setAddKind(d.kind); setAddName(d.name || ''); setAddUrl(d.sourceUrl || ''); setAddText(d.sourceText || ''); setFileRef(null); setError(''); };

  const submit = async () => {
    haptic('medium'); setSaving(true); setError('');
    try {
      if (editingId) {
        const payload: any = {};
        if (addName.trim()) payload.name = addName.trim();
        if (addKind === 'url') { if (!addUrl.trim()) { setError('Введите URL'); setSaving(false); return; } payload.url = addUrl.trim(); }
        else if (addKind === 'text') { if (!addText.trim()) { setError('Введите текст'); setSaving(false); return; } payload.text = addText; }
        else if (addKind === 'file' && fileRef) { const data = await fileToBase64(fileRef); payload.mimeType = fileRef.type || 'application/octet-stream'; payload.data = data; if (!payload.name) payload.name = fileRef.name; }
        await documentsApi.update(editingId, payload);
      } else if (addKind === 'url') {
        if (!addUrl.trim()) { setError('Введите URL'); setSaving(false); return; }
        await documentsApi.addUrl({ url: addUrl.trim(), name: addName.trim() || undefined });
      } else if (addKind === 'text') {
        if (!addText.trim()) { setError('Введите текст'); setSaving(false); return; }
        await documentsApi.addText({ text: addText, name: addName.trim() || undefined });
      } else if (addKind === 'file' && fileRef) {
        const data = await fileToBase64(fileRef);
        await documentsApi.addFile({ name: fileRef.name, mimeType: fileRef.type || 'application/octet-stream', data });
      } else if (addKind === 'file' && !fileRef) { setError('Выберите файл'); setSaving(false); return; }
      resetForm(); haptic('heavy'); load();
    } catch (e: any) { setError(e?.message || 'Ошибка'); } finally { setSaving(false); }
  };

  const doReindex = async (id: string) => { haptic('medium'); setReindexing(id); try { await documentsApi.reindex(id); load(); } catch {} finally { setReindexing(null); } };
  const remove = async (id: string) => { haptic('heavy'); try { await documentsApi.remove(id); setDocs((prev) => prev.filter((d) => d.id !== id)); } catch {} };

  const KindIcon = ({ kind }: { kind: string }) => { const Icon = KIND_ICONS[kind] || BookOpen; return <Icon />; };

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">База знаний</div>
        {!adding && <button className="rf-back" onClick={startAdd}><Plus />Добавить</button>}
      </div>
      <div className="rf-detail-scroll">
        <div className="rf-info"><BookOpen /><div><b>Память ассистента</b><br />Документы для контекста. Файлы, ссылки или текст — обрабатываются через RAG для точных ответов.</div></div>

        {adding ? (
          <div className="rf-card rf-acard rf-mb12">
            {!editingId && (
              <div className="rf-seg">
                <button className={'rf-seg-btn' + (addKind === 'url' ? ' active' : '')} onClick={() => { haptic('light'); setAddKind('url'); }}>Ссылка</button>
                <button className={'rf-seg-btn' + (addKind === 'text' ? ' active' : '')} onClick={() => { haptic('light'); setAddKind('text'); }}>Текст</button>
                <button className={'rf-seg-btn' + (addKind === 'file' ? ' active' : '')} onClick={() => { haptic('light'); setAddKind('file'); }}>Файл</button>
              </div>
            )}
            {addKind === 'url' && (<>
              <div className="rf-field"><label>URL</label><input className="rf-input" value={addUrl} onChange={(e) => setAddUrl(e.target.value)} placeholder="https://example.com/doc" /></div>
              <div className="rf-field"><label>Название (необязательно)</label><input className="rf-input" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Как назвать в базе" /></div>
            </>)}
            {addKind === 'text' && (<>
              <div className="rf-field"><label>Название (необязательно)</label><input className="rf-input" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Как назвать в базе" /></div>
              <div className="rf-field"><label>Текст</label><textarea className="rf-textarea" value={addText} onChange={(e) => setAddText(e.target.value)} placeholder="Вставьте текст..." rows={6} /></div>
            </>)}
            {addKind === 'file' && (<>
              <div className="rf-field"><label>Название (необязательно)</label><input className="rf-input" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Как назвать в базе" /></div>
              <div className="rf-field"><label>{editingId ? 'Новый файл (необязательно)' : 'Файл'}</label><input className="rf-file" ref={fileInputRef} type="file" onChange={(e) => setFileRef(e.target.files?.[0] ?? null)} />
              {fileRef && <div className="rf-hint">Выбран: {fileRef.name} ({Math.round(fileRef.size / 1024)} КБ)</div>}
              {editingId && !fileRef && <div className="rf-hint">Оставьте пустым, чтобы изменить только название</div>}</div>
            </>)}
            {error && <div className="rf-result err"><AlertCircle /><div>{error}</div></div>}
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={submit} disabled={saving}>{saving ? <Loader2 className="spin" /> : editingId ? <Save /> : <Upload />}{editingId ? 'Сохранить' : 'Добавить'}</button>
              <button className="rf-btn" onClick={resetForm}><X />Отмена</button>
            </div>
          </div>
        ) : loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : docs.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><BookOpen /></div><h3>Документов нет</h3><p>Добавьте файлы, ссылки или текст</p></div>
        ) : (
          <div className="rf-rows rf-stagger">
            {docs.map((d) => (
              <div key={d.id} className="rf-card rf-conn">
                <div className="rf-conn-row">
                  <div className="rf-row-ic"><KindIcon kind={d.kind} /></div>
                  <div className="rf-conn-info">
                    <div className="rf-conn-name">{d.name}</div>
                    <div className="rf-conn-sub">{d.kind}{d.chunkCount ? ' · ' + d.chunkCount + ' чанков' : ''}{d.error ? ' · ' + d.error : ''}</div>
                  </div>
                  <span className={'rf-pill ' + (d.status === 'ready' ? 'on' : d.status === 'error' ? 'err' : 'run')}><span className="dot" />{statusLabel(d.status)}</span>
                </div>
                <div className="rf-acts">
                  <button className="rf-btn sm icon" title="Изменить" onClick={() => startEdit(d)}><Edit3 /></button>
                  <button className="rf-btn sm icon" title="Переиндексировать" onClick={() => { haptic('light'); setConfirmId(d.id); }} disabled={reindexing === d.id}>{reindexing === d.id ? <Loader2 className="spin" /> : <RefreshCw />}</button>
                  <button className="rf-btn sm danger icon" title="Удалить" onClick={() => remove(d.id)}><Trash2 /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmId && (
        <div className="rf-sheet-backdrop" onClick={() => setConfirmId(null)}>
          <div className="rf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="rf-sheet-handle" />
            <div className="rf-sheet-head"><div className="rf-sheet-title">Переиндексировать документ?</div></div>
            <div className="rf-sheet-body">Переиндексация заново разбивает документ на фрагменты и пересчитывает векторные представления (эмбеддинги). <b>Это нужно нечасто</b> — например, если документ обновился или прошлая индексация завершилась с ошибкой. Процесс может занять время.</div>
            <div className="rf-sheet-actions">
              <button className="rf-btn" onClick={() => setConfirmId(null)}><X />Отмена</button>
              <button className="rf-btn primary" onClick={() => { const id = confirmId; setConfirmId(null); if (id) doReindex(id); }}><RefreshCw />Подтвердить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
